import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, PhoneIncoming, PhoneMissed, PhoneOutgoing, Video } from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/ru";
import { api } from "../api/client";
import { Avatar } from "../components/Avatar";
import { ru } from "../locales/ru";

dayjs.extend(relativeTime);
dayjs.locale("ru");

const failedStatuses = ["missed", "declined", "failed", "busy"];

const fmtDuration = (sec) =>
  sec == null ? null : `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;

export default function Calls() {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    api.get("/calls/history", { params: { limit: 20 } })
      .then(({ data }) => {
        setCalls(data.calls);
        setHasMore(data.has_more);
      })
      .catch(() => {});
  }, []);

  const loadMore = async () => {
    const last = calls[calls.length - 1];
    if (!last) return;
    try {
      const { data } = await api.get("/calls/history", { params: { before: last.started_at, limit: 20 } });
      setCalls((prev) => [...prev, ...data.calls]);
      setHasMore(data.has_more);
    } catch {}
  };

  return (
    <div className="pt-8 pb-28" data-testid="calls-page">
      <div className="flex items-center gap-3 px-6 mb-5">
        <button
          data-testid="calls-back-button"
          onClick={() => navigate("/chats")}
          className="p-2 rounded-full bg-gray-100 active:scale-[0.95] transition-transform"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-3xl font-extrabold tracking-tight">{ru.calls.title}</h1>
      </div>

      {calls.length === 0 && (
        <p className="px-6 text-gray-400 text-sm" data-testid="calls-empty">
          {ru.calls.empty}
        </p>
      )}

      <div>
        {calls.map((c) => {
          const bad = failedStatuses.includes(c.status);
          const Icon = bad ? PhoneMissed : c.direction === "outgoing" ? PhoneOutgoing : PhoneIncoming;
          const duration = fmtDuration(c.duration_sec);          return (
            <button
              key={c.id}
              data-testid={`call-row-${c.id}`}
              onClick={() => c.peer && navigate(`/chats/u/${c.peer.id}`)}
              className="w-full flex items-center gap-3 px-6 py-3.5 text-left active:bg-gray-50 transition-colors"
            >
              <Avatar avatar={c.peer?.avatar} name={c.peer?.name} size={44} />
              <div className="min-w-0 flex-1">
                <p className="font-bold truncate">{c.peer?.name || ru.calls.unknown}</p>
                <p className={`text-sm flex items-center gap-1.5 ${bad ? "text-[#FF3B30]" : "text-gray-500"}`}>
                  <Icon size={13} />
                  {c.media === "video" && <Video size={13} data-testid={`call-media-video-${c.id}`} />}
                  {ru.calls.status[c.status] || c.status}
                  {duration && ` · ${duration}`}
                </p>
              </div>
              <span className="text-xs text-gray-400 shrink-0">{dayjs(c.started_at).fromNow(true)}</span>
            </button>
          );
        })}
      </div>

      {hasMore && (
        <div className="px-6">
          <button
            data-testid="calls-load-more"
            onClick={loadMore}
            className="mt-3 w-full h-11 bg-gray-100 rounded-2xl text-sm font-semibold active:scale-[0.98] transition-transform"
          >
            {ru.orders.loadMore}
          </button>
        </div>
      )}
    </div>
  );
}
